import Phaser from 'phaser';

// Stardew Valley-inspired color palette
const C = {
  // Greens
  grassDark: '#3e6139',
  grass: '#4a7c4b',
  grassLight: '#6aaa3a',
  leafDark: '#2d5a1e',
  leaf: '#5a9e2f',

  // Browns
  dirt: '#8b6914',
  dirtLight: '#a5824a',
  wood: '#6b4423',
  woodLight: '#8b6b43',
  bark: '#4a3219',

  // Sky/Water
  skyBlue: '#6eb5ff',
  water: '#4a90d9',
  waterDark: '#3a70b9',
  waterLight: '#7ec8ff',

  // Warm
  gold: '#e8a038',
  orange: '#d4873b',
  red: '#d4573b',
  redDark: '#a83828',

  // Night
  nightDark: '#2d1b69',
  nightMid: '#5b3a8c',
  nightLight: '#8b68c4',
  purple: '#9955cc',

  // Neutrals
  white: '#ffffff',
  lightGray: '#cccccc',
  gray: '#888888',
  darkGray: '#444444',
  black: '#000000',

  // Plant specific
  sunYellow: '#ffdd44',
  sunOrange: '#ffaa22',
  moonPurple: '#aa77ee',
  moonBlue: '#6677cc',
  firePepperRed: '#ff4422',
  firePepperOrange: '#ff8833',
  shadowDark: '#1a2a1a',
  shadowGreen: '#2a4a2a',
  appleRed: '#cc3333',
  appleGreen: '#55aa33',
  pearYellow: '#cccc44',
  pearGreen: '#77aa44',

  // Skin/clothing
  skin: '#f0c8a0',
  skinShadow: '#d4a878',
  hat: '#cc6633',
  hatBand: '#884422',
  shirt: '#5577cc',
  overalls: '#4466aa',
  boots: '#553322',
  hair: '#664422',
};

function hexToNum(hex: string): number {
  return parseInt(hex.replace('#', ''), 16);
}

type Ctx = CanvasRenderingContext2D;

function px(ctx: Ctx, x: number, y: number, color: string): void {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, 1, 1);
}

function rect(ctx: Ctx, x: number, y: number, w: number, h: number, color: string): void {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, h);
}

export class AssetGenerator {
  static generateAll(scene: Phaser.Scene): void {
    AssetGenerator.generatePlayer(scene);
    AssetGenerator.generatePlants(scene);
    AssetGenerator.generateTerrain(scene);
    AssetGenerator.generateObjects(scene);
    AssetGenerator.generateUI(scene);
    AssetGenerator.generateMonsters(scene);
    AssetGenerator.generateWeapons(scene);
  }

  // =========================================================================
  // PLAYER SPRITE SHEET: 4 columns x 4 rows = 16 frames (16x16 each)
  // Rows: down, left, right, up
  // Cols: 4 walk frames
  // =========================================================================
  static generatePlayer(scene: Phaser.Scene): void {
    const cols = 4, rows = 4, size = 16;
    const canvas = scene.textures.createCanvas('player', cols * size, rows * size)!;
    const ctx = canvas.context;

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const ox = col * size;
        const oy = row * size;
        AssetGenerator.drawPlayerFrame(ctx, ox, oy, row, col);
      }
    }

    canvas.refresh();

    // Add frame data
    const texture = scene.textures.get('player');
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        texture.add(row * cols + col, 0, col * size, row * size, size, size);
      }
    }
  }

  private static drawPlayerFrame(ctx: Ctx, ox: number, oy: number, dir: number, frame: number): void {
    // Body bounce
    const bounce = (frame % 2 === 1) ? -1 : 0;
    // Leg animation
    const legOffset = frame % 2 === 0 ? 0 : 1;

    // -- Hat --
    rect(ctx, ox + 4, oy + 1 + bounce, 8, 3, C.hat);
    rect(ctx, ox + 3, oy + 3 + bounce, 10, 1, C.hat); // brim
    rect(ctx, ox + 5, oy + 2 + bounce, 6, 1, C.hatBand);

    // -- Hair --
    if (dir === 0 || dir === 3) { // front/back
      rect(ctx, ox + 4, oy + 4 + bounce, 8, 1, C.hair);
    } else {
      rect(ctx, ox + 4, oy + 4 + bounce, 7, 1, C.hair);
    }

    // -- Head --
    rect(ctx, ox + 5, oy + 4 + bounce, 6, 4, C.skin);

    // -- Eyes --
    if (dir === 0) { // facing down
      px(ctx, ox + 6, oy + 6 + bounce, C.black);
      px(ctx, ox + 9, oy + 6 + bounce, C.black);
    } else if (dir === 3) { // facing up
      // No eyes visible from behind
    } else if (dir === 1) { // facing left
      px(ctx, ox + 5, oy + 6 + bounce, C.black);
    } else { // facing right
      px(ctx, ox + 10, oy + 6 + bounce, C.black);
    }

    // -- Shirt --
    rect(ctx, ox + 5, oy + 8 + bounce, 6, 2, C.shirt);

    // -- Overalls --
    rect(ctx, ox + 5, oy + 10 + bounce, 6, 2, C.overalls);
    // Straps
    px(ctx, ox + 6, oy + 8 + bounce, C.overalls);
    px(ctx, ox + 9, oy + 8 + bounce, C.overalls);

    // -- Arms --
    if (dir === 1) { // left
      rect(ctx, ox + 4, oy + 8 + bounce, 1, 3, C.skin);
    } else if (dir === 2) { // right
      rect(ctx, ox + 11, oy + 8 + bounce, 1, 3, C.skin);
    } else {
      rect(ctx, ox + 4, oy + 8 + bounce, 1, 3, C.skin);
      rect(ctx, ox + 11, oy + 8 + bounce, 1, 3, C.skin);
    }

    // -- Legs/Boots --
    if (frame % 2 === 0) {
      rect(ctx, ox + 5, oy + 12 + bounce, 2, 2, C.boots);
      rect(ctx, ox + 9, oy + 12 + bounce, 2, 2, C.boots);
    } else {
      rect(ctx, ox + 4 + legOffset, oy + 12 + bounce, 2, 2, C.boots);
      rect(ctx, ox + 10 - legOffset, oy + 12 + bounce, 2, 2, C.boots);
    }
  }

  // =========================================================================
  // PLANTS: 11 rows x 4 cols = 44 frames (16x16 each)
  // Rows 0-5: base plants, rows 6-10: hybrid plants
  // =========================================================================
  static generatePlants(scene: Phaser.Scene): void {
    const cols = 4, rows = 11, size = 16;
    const canvas = scene.textures.createCanvas('plants', cols * size, rows * size)!;
    const ctx = canvas.context;

    const plantDrawers = [
      AssetGenerator.drawSunflower,
      AssetGenerator.drawMoonbloom,
      AssetGenerator.drawFirePepper,
      AssetGenerator.drawShadowVine,
      AssetGenerator.drawAppleTree,
      AssetGenerator.drawPearTree,
      AssetGenerator.drawPeapple,
      AssetGenerator.drawTwilightBlossom,
      AssetGenerator.drawInfernalCreeper,
      AssetGenerator.drawSolarFlare,
      AssetGenerator.drawVoidOrchid,
    ];

    for (let row = 0; row < rows; row++) {
      for (let stage = 0; stage < cols; stage++) {
        const ox = stage * size;
        const oy = row * size;
        plantDrawers[row](ctx, ox, oy, stage);
      }
    }

    canvas.refresh();

    const texture = scene.textures.get('plants');
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        texture.add(row * cols + col, 0, col * size, row * size, size, size);
      }
    }
  }

  // -- Sunflower (Day) --
  private static drawSunflower(ctx: Ctx, ox: number, oy: number, stage: number): void {
    if (stage === 0) { // Seed
      rect(ctx, ox + 7, oy + 13, 2, 2, C.dirt);
      px(ctx, ox + 7, oy + 12, C.grassLight);
    } else if (stage === 1) { // Sprout
      rect(ctx, ox + 7, oy + 10, 2, 5, C.grassLight);
      px(ctx, ox + 6, oy + 9, C.leaf);
      px(ctx, ox + 9, oy + 10, C.leaf);
    } else if (stage === 2) { // Growing
      rect(ctx, ox + 7, oy + 6, 2, 9, C.grassDark);
      rect(ctx, ox + 5, oy + 8, 2, 3, C.leaf);
      rect(ctx, ox + 9, oy + 7, 2, 3, C.leaf);
      rect(ctx, ox + 6, oy + 3, 4, 4, C.sunYellow);
    } else { // Mature
      rect(ctx, ox + 7, oy + 5, 2, 10, C.grassDark);
      // Leaves
      rect(ctx, ox + 4, oy + 8, 3, 2, C.leaf);
      rect(ctx, ox + 9, oy + 7, 3, 2, C.leaf);
      rect(ctx, ox + 5, oy + 11, 2, 2, C.leaf);
      // Flower
      rect(ctx, ox + 5, oy + 1, 6, 5, C.sunYellow);
      rect(ctx, ox + 6, oy + 0, 4, 1, C.sunYellow);
      rect(ctx, ox + 6, oy + 6, 4, 1, C.sunYellow);
      rect(ctx, ox + 4, oy + 2, 1, 3, C.sunOrange);
      rect(ctx, ox + 11, oy + 2, 1, 3, C.sunOrange);
      // Center
      rect(ctx, ox + 7, oy + 2, 2, 3, C.dirt);
    }
  }

  // -- Moonbloom (Night) --
  private static drawMoonbloom(ctx: Ctx, ox: number, oy: number, stage: number): void {
    if (stage === 0) {
      rect(ctx, ox + 7, oy + 13, 2, 2, C.nightDark);
      px(ctx, ox + 7, oy + 12, C.nightLight);
    } else if (stage === 1) {
      rect(ctx, ox + 7, oy + 10, 2, 5, C.nightMid);
      px(ctx, ox + 6, oy + 9, C.nightLight);
      px(ctx, ox + 9, oy + 10, C.nightLight);
    } else if (stage === 2) {
      rect(ctx, ox + 7, oy + 6, 2, 9, C.nightMid);
      rect(ctx, ox + 5, oy + 8, 2, 2, C.nightLight);
      rect(ctx, ox + 9, oy + 7, 2, 2, C.nightLight);
      rect(ctx, ox + 6, oy + 3, 4, 3, C.moonPurple);
    } else {
      rect(ctx, ox + 7, oy + 5, 2, 10, C.nightMid);
      rect(ctx, ox + 4, oy + 8, 3, 2, C.nightLight);
      rect(ctx, ox + 9, oy + 7, 3, 2, C.nightLight);
      // Flower - crescent moon shape
      rect(ctx, ox + 5, oy + 0, 6, 6, C.moonPurple);
      rect(ctx, ox + 6, oy + 1, 4, 4, C.moonBlue);
      rect(ctx, ox + 7, oy + 2, 3, 2, C.nightDark); // crescent cutout
      // Glow pixels
      px(ctx, ox + 4, oy + 2, C.white);
      px(ctx, ox + 11, oy + 3, C.white);
      px(ctx, ox + 6, oy + 0, C.white);
    }
  }

  // -- Fire Pepper (Day) --
  private static drawFirePepper(ctx: Ctx, ox: number, oy: number, stage: number): void {
    if (stage === 0) {
      rect(ctx, ox + 7, oy + 13, 2, 2, C.redDark);
      px(ctx, ox + 7, oy + 12, C.red);
    } else if (stage === 1) {
      rect(ctx, ox + 7, oy + 10, 2, 5, C.grassLight);
      px(ctx, ox + 6, oy + 9, C.red);
      px(ctx, ox + 9, oy + 10, C.grassLight);
    } else if (stage === 2) {
      rect(ctx, ox + 7, oy + 7, 2, 8, C.grassDark);
      rect(ctx, ox + 5, oy + 9, 2, 2, C.leaf);
      rect(ctx, ox + 9, oy + 8, 2, 2, C.leaf);
      // Small pepper
      rect(ctx, ox + 6, oy + 4, 2, 4, C.red);
      px(ctx, ox + 6, oy + 3, C.grassDark);
    } else {
      rect(ctx, ox + 7, oy + 5, 2, 10, C.grassDark);
      rect(ctx, ox + 4, oy + 8, 3, 2, C.leaf);
      rect(ctx, ox + 9, oy + 7, 3, 2, C.leaf);
      // Big pepper
      rect(ctx, ox + 5, oy + 2, 3, 6, C.firePepperRed);
      rect(ctx, ox + 8, oy + 3, 3, 5, C.firePepperRed);
      px(ctx, ox + 6, oy + 1, C.grassDark); // stem
      // Fire highlights
      px(ctx, ox + 5, oy + 4, C.firePepperOrange);
      px(ctx, ox + 9, oy + 5, C.firePepperOrange);
      px(ctx, ox + 7, oy + 2, C.sunYellow);
    }
  }

  // -- Shadow Vine (Night) --
  private static drawShadowVine(ctx: Ctx, ox: number, oy: number, stage: number): void {
    if (stage === 0) {
      rect(ctx, ox + 7, oy + 13, 2, 2, C.shadowDark);
      px(ctx, ox + 7, oy + 12, C.shadowGreen);
    } else if (stage === 1) {
      rect(ctx, ox + 7, oy + 10, 2, 5, C.shadowGreen);
      px(ctx, ox + 6, oy + 11, C.shadowDark);
      px(ctx, ox + 9, oy + 9, C.shadowGreen);
    } else if (stage === 2) {
      // Vine tendrils
      rect(ctx, ox + 7, oy + 5, 2, 10, C.shadowGreen);
      px(ctx, ox + 5, oy + 7, C.shadowGreen);
      px(ctx, ox + 4, oy + 6, C.shadowGreen);
      px(ctx, ox + 9, oy + 8, C.shadowGreen);
      px(ctx, ox + 10, oy + 9, C.shadowGreen);
      rect(ctx, ox + 6, oy + 3, 4, 3, C.shadowDark);
    } else {
      // Full vine
      rect(ctx, ox + 7, oy + 4, 2, 11, C.shadowGreen);
      // Tendrils spreading
      px(ctx, ox + 5, oy + 6, C.shadowGreen);
      px(ctx, ox + 4, oy + 5, C.shadowGreen);
      px(ctx, ox + 3, oy + 4, C.shadowGreen);
      px(ctx, ox + 9, oy + 7, C.shadowGreen);
      px(ctx, ox + 10, oy + 8, C.shadowGreen);
      px(ctx, ox + 11, oy + 9, C.shadowGreen);
      px(ctx, ox + 5, oy + 10, C.shadowGreen);
      px(ctx, ox + 10, oy + 5, C.shadowGreen);
      // Dark flower
      rect(ctx, ox + 5, oy + 1, 6, 4, C.shadowDark);
      rect(ctx, ox + 6, oy + 0, 4, 1, C.nightDark);
      // Evil eyes
      px(ctx, ox + 6, oy + 2, C.nightLight);
      px(ctx, ox + 9, oy + 2, C.nightLight);
    }
  }

  // -- Apple Tree (Day) --
  private static drawAppleTree(ctx: Ctx, ox: number, oy: number, stage: number): void {
    if (stage === 0) {
      rect(ctx, ox + 7, oy + 13, 2, 2, C.wood);
      px(ctx, ox + 7, oy + 12, C.appleGreen);
    } else if (stage === 1) {
      rect(ctx, ox + 7, oy + 9, 2, 6, C.wood);
      rect(ctx, ox + 6, oy + 7, 4, 3, C.appleGreen);
    } else if (stage === 2) {
      rect(ctx, ox + 7, oy + 8, 2, 7, C.wood);
      rect(ctx, ox + 4, oy + 4, 8, 5, C.appleGreen);
      rect(ctx, ox + 5, oy + 3, 6, 1, C.leaf);
    } else {
      // Trunk
      rect(ctx, ox + 7, oy + 8, 2, 7, C.wood);
      rect(ctx, ox + 6, oy + 11, 4, 1, C.bark);
      // Crown
      rect(ctx, ox + 3, oy + 3, 10, 6, C.appleGreen);
      rect(ctx, ox + 4, oy + 2, 8, 1, C.leaf);
      rect(ctx, ox + 5, oy + 1, 6, 1, C.leaf);
      // Apples
      px(ctx, ox + 4, oy + 4, C.appleRed);
      px(ctx, ox + 8, oy + 3, C.appleRed);
      px(ctx, ox + 11, oy + 5, C.appleRed);
      px(ctx, ox + 6, oy + 6, C.appleRed);
      px(ctx, ox + 10, oy + 7, C.appleRed);
    }
  }

  // -- Pear Tree (Day) --
  private static drawPearTree(ctx: Ctx, ox: number, oy: number, stage: number): void {
    if (stage === 0) {
      rect(ctx, ox + 7, oy + 13, 2, 2, C.wood);
      px(ctx, ox + 7, oy + 12, C.pearGreen);
    } else if (stage === 1) {
      rect(ctx, ox + 7, oy + 9, 2, 6, C.wood);
      rect(ctx, ox + 6, oy + 7, 4, 3, C.pearGreen);
    } else if (stage === 2) {
      rect(ctx, ox + 7, oy + 8, 2, 7, C.wood);
      rect(ctx, ox + 4, oy + 4, 8, 5, C.pearGreen);
      rect(ctx, ox + 5, oy + 3, 6, 1, C.leaf);
    } else {
      // Trunk
      rect(ctx, ox + 7, oy + 8, 2, 7, C.wood);
      rect(ctx, ox + 6, oy + 11, 4, 1, C.bark);
      // Crown (pointy pear tree shape)
      rect(ctx, ox + 4, oy + 4, 8, 5, C.pearGreen);
      rect(ctx, ox + 5, oy + 2, 6, 2, C.leaf);
      rect(ctx, ox + 6, oy + 1, 4, 1, C.leaf);
      rect(ctx, ox + 7, oy + 0, 2, 1, C.leafDark);
      // Pears (teardrop shape)
      px(ctx, ox + 5, oy + 5, C.pearYellow);
      px(ctx, ox + 5, oy + 6, C.pearYellow);
      px(ctx, ox + 9, oy + 4, C.pearYellow);
      px(ctx, ox + 9, oy + 5, C.pearYellow);
      px(ctx, ox + 11, oy + 6, C.pearYellow);
      px(ctx, ox + 7, oy + 7, C.pearYellow);
    }
  }

  // -- Peapple Hybrid (Apple + Pear) --
  private static drawPeapple(ctx: Ctx, ox: number, oy: number, stage: number): void {
    if (stage === 0) {
      rect(ctx, ox + 7, oy + 13, 2, 2, C.wood);
      px(ctx, ox + 7, oy + 12, '#88aa44');
    } else if (stage === 1) {
      rect(ctx, ox + 7, oy + 9, 2, 6, C.wood);
      rect(ctx, ox + 5, oy + 7, 6, 3, '#88aa44');
    } else if (stage === 2) {
      rect(ctx, ox + 7, oy + 8, 2, 7, C.wood);
      rect(ctx, ox + 4, oy + 4, 8, 5, '#88aa44');
      rect(ctx, ox + 5, oy + 3, 6, 1, C.leaf);
      px(ctx, ox + 5, oy + 5, C.appleRed);
      px(ctx, ox + 9, oy + 6, C.pearYellow);
    } else {
      rect(ctx, ox + 7, oy + 8, 2, 7, C.wood);
      rect(ctx, ox + 6, oy + 11, 4, 1, C.bark);
      rect(ctx, ox + 3, oy + 3, 10, 6, '#88aa44');
      rect(ctx, ox + 4, oy + 2, 8, 1, C.leaf);
      rect(ctx, ox + 5, oy + 1, 6, 1, C.leaf);
      // Mixed fruits
      px(ctx, ox + 4, oy + 4, C.appleRed);
      px(ctx, ox + 8, oy + 3, C.pearYellow);
      px(ctx, ox + 11, oy + 5, C.appleRed);
      px(ctx, ox + 6, oy + 6, C.pearYellow);
      px(ctx, ox + 10, oy + 7, C.appleRed);
      px(ctx, ox + 4, oy + 7, C.pearYellow);
    }
  }

  // -- Twilight Blossom Hybrid (Sunflower + Moonbloom) --
  private static drawTwilightBlossom(ctx: Ctx, ox: number, oy: number, stage: number): void {
    if (stage === 0) {
      rect(ctx, ox + 7, oy + 13, 2, 2, '#665544');
      px(ctx, ox + 7, oy + 12, '#aa88cc');
    } else if (stage === 1) {
      rect(ctx, ox + 7, oy + 10, 2, 5, '#665588');
      px(ctx, ox + 6, oy + 9, '#ddaa44');
      px(ctx, ox + 9, oy + 10, '#8866aa');
    } else if (stage === 2) {
      rect(ctx, ox + 7, oy + 6, 2, 9, '#665588');
      rect(ctx, ox + 5, oy + 8, 2, 3, '#ddaa44');
      rect(ctx, ox + 9, oy + 7, 2, 3, '#8866aa');
      // Half-flower
      rect(ctx, ox + 5, oy + 3, 3, 3, C.sunYellow);
      rect(ctx, ox + 8, oy + 3, 3, 3, C.moonPurple);
    } else {
      rect(ctx, ox + 7, oy + 5, 2, 10, '#665588');
      rect(ctx, ox + 4, oy + 8, 3, 2, '#ddaa44');
      rect(ctx, ox + 9, oy + 7, 3, 2, '#8866aa');
      // Left = sun, right = moon
      rect(ctx, ox + 4, oy + 1, 4, 5, C.sunYellow);
      rect(ctx, ox + 8, oy + 1, 4, 5, C.moonPurple);
      rect(ctx, ox + 5, oy + 0, 2, 1, C.sunOrange);
      rect(ctx, ox + 9, oy + 0, 2, 1, C.moonBlue);
      // Center
      rect(ctx, ox + 7, oy + 2, 2, 3, '#ffaadd');
      // Glow
      px(ctx, ox + 3, oy + 3, C.white);
      px(ctx, ox + 12, oy + 2, C.white);
    }
  }

  // -- Infernal Creeper Hybrid (Fire Pepper + Shadow Vine) --
  private static drawInfernalCreeper(ctx: Ctx, ox: number, oy: number, stage: number): void {
    if (stage === 0) {
      rect(ctx, ox + 7, oy + 13, 2, 2, '#3a1a0a');
      px(ctx, ox + 7, oy + 12, '#ff6622');
    } else if (stage === 1) {
      rect(ctx, ox + 7, oy + 10, 2, 5, '#2a4a2a');
      px(ctx, ox + 6, oy + 9, '#ff4422');
      px(ctx, ox + 9, oy + 10, '#2a4a2a');
    } else if (stage === 2) {
      rect(ctx, ox + 7, oy + 5, 2, 10, '#2a4a2a');
      px(ctx, ox + 5, oy + 7, '#2a4a2a');
      px(ctx, ox + 10, oy + 8, '#2a4a2a');
      // Fire tips on vines
      px(ctx, ox + 5, oy + 6, '#ff6622');
      px(ctx, ox + 10, oy + 7, '#ff6622');
      rect(ctx, ox + 6, oy + 2, 4, 4, '#3a1a0a');
      px(ctx, ox + 7, oy + 1, '#ff4422');
    } else {
      rect(ctx, ox + 7, oy + 4, 2, 11, '#2a4a2a');
      // Spreading tendrils with fire
      px(ctx, ox + 5, oy + 6, '#2a4a2a');
      px(ctx, ox + 4, oy + 5, '#ff6622');
      px(ctx, ox + 3, oy + 4, '#ff4422');
      px(ctx, ox + 9, oy + 7, '#2a4a2a');
      px(ctx, ox + 10, oy + 8, '#ff6622');
      px(ctx, ox + 11, oy + 9, '#ff4422');
      px(ctx, ox + 5, oy + 10, '#ff8833');
      px(ctx, ox + 10, oy + 5, '#ff8833');
      // Flaming head
      rect(ctx, ox + 5, oy + 0, 6, 5, '#3a1a0a');
      rect(ctx, ox + 6, oy + 0, 4, 2, C.firePepperRed);
      px(ctx, ox + 4, oy + 1, '#ff8833');
      px(ctx, ox + 11, oy + 1, '#ff8833');
      // Fire eyes
      px(ctx, ox + 6, oy + 2, '#ffaa00');
      px(ctx, ox + 9, oy + 2, '#ffaa00');
    }
  }

  // -- Solar Flare Hybrid (Sunflower + Fire Pepper) --
  private static drawSolarFlare(ctx: Ctx, ox: number, oy: number, stage: number): void {
    if (stage === 0) {
      rect(ctx, ox + 7, oy + 13, 2, 2, C.dirt);
      px(ctx, ox + 7, oy + 12, '#ffaa22');
    } else if (stage === 1) {
      rect(ctx, ox + 7, oy + 10, 2, 5, '#886622');
      px(ctx, ox + 6, oy + 9, '#ffaa22');
      px(ctx, ox + 9, oy + 10, '#ff6622');
    } else if (stage === 2) {
      rect(ctx, ox + 7, oy + 6, 2, 9, '#886622');
      rect(ctx, ox + 5, oy + 8, 2, 3, '#ffaa22');
      rect(ctx, ox + 9, oy + 7, 2, 3, '#ff6622');
      rect(ctx, ox + 5, oy + 2, 6, 5, C.sunYellow);
      rect(ctx, ox + 6, oy + 3, 4, 3, '#ffaa22');
    } else {
      rect(ctx, ox + 7, oy + 5, 2, 10, '#886622');
      rect(ctx, ox + 4, oy + 8, 3, 2, '#ffaa22');
      rect(ctx, ox + 9, oy + 7, 3, 2, '#ff6622');
      // Blazing sun flower
      rect(ctx, ox + 4, oy + 1, 8, 5, C.sunYellow);
      rect(ctx, ox + 5, oy + 0, 6, 1, '#ffaa22');
      rect(ctx, ox + 5, oy + 6, 6, 1, '#ffaa22');
      rect(ctx, ox + 3, oy + 2, 1, 3, C.firePepperOrange);
      rect(ctx, ox + 12, oy + 2, 1, 3, C.firePepperOrange);
      // White-hot center
      rect(ctx, ox + 7, oy + 2, 2, 3, C.white);
      px(ctx, ox + 6, oy + 3, '#ffffaa');
      px(ctx, ox + 9, oy + 3, '#ffffaa');
      // Fire corona
      px(ctx, ox + 4, oy + 0, C.firePepperRed);
      px(ctx, ox + 11, oy + 0, C.firePepperRed);
      px(ctx, ox + 3, oy + 5, C.firePepperRed);
      px(ctx, ox + 12, oy + 5, C.firePepperRed);
    }
  }

  // -- Void Orchid Hybrid (Moonbloom + Shadow Vine) --
  private static drawVoidOrchid(ctx: Ctx, ox: number, oy: number, stage: number): void {
    if (stage === 0) {
      rect(ctx, ox + 7, oy + 13, 2, 2, '#1a0a2a');
      px(ctx, ox + 7, oy + 12, '#6644aa');
    } else if (stage === 1) {
      rect(ctx, ox + 7, oy + 10, 2, 5, '#2a1a3a');
      px(ctx, ox + 6, oy + 9, '#6644aa');
      px(ctx, ox + 9, oy + 10, '#4422aa');
    } else if (stage === 2) {
      rect(ctx, ox + 7, oy + 5, 2, 10, '#2a1a3a');
      px(ctx, ox + 5, oy + 7, '#4422aa');
      px(ctx, ox + 10, oy + 8, '#4422aa');
      rect(ctx, ox + 5, oy + 2, 6, 4, '#3a1a4a');
      px(ctx, ox + 7, oy + 1, '#8855cc');
    } else {
      rect(ctx, ox + 7, oy + 5, 2, 10, '#2a1a3a');
      // Dark tendrils
      px(ctx, ox + 5, oy + 7, '#2a1a3a');
      px(ctx, ox + 4, oy + 6, '#1a0a2a');
      px(ctx, ox + 10, oy + 8, '#2a1a3a');
      px(ctx, ox + 11, oy + 9, '#1a0a2a');
      // Orchid flower (deep void)
      rect(ctx, ox + 4, oy + 0, 8, 6, '#2a0a3a');
      rect(ctx, ox + 5, oy + 1, 6, 4, '#3a1a4a');
      // Petals
      px(ctx, ox + 4, oy + 2, '#6644aa');
      px(ctx, ox + 11, oy + 2, '#6644aa');
      px(ctx, ox + 7, oy + 0, '#8855cc');
      px(ctx, ox + 8, oy + 0, '#8855cc');
      // Void center
      rect(ctx, ox + 7, oy + 2, 2, 2, '#000011');
      // Ethereal glow dots
      px(ctx, ox + 3, oy + 3, '#aaaaff');
      px(ctx, ox + 12, oy + 1, '#aaaaff');
      px(ctx, ox + 6, oy + 5, '#8888ff');
      px(ctx, ox + 9, oy + 5, '#8888ff');
    }
  }

  // =========================================================================
  // TERRAIN TILESET: 8 cols x 4 rows = 32 tiles (16x16 each)
  // =========================================================================
  static generateTerrain(scene: Phaser.Scene): void {
    const cols = 8, rows = 6, size = 16;
    const canvas = scene.textures.createCanvas('terrain', cols * size, rows * size)!;
    const ctx = canvas.context;

    // Row 0: Grass (0-3), Dirt (4-7)
    for (let i = 0; i < 4; i++) {
      AssetGenerator.drawGrassTile(ctx, i * size, 0, i);
    }
    for (let i = 0; i < 4; i++) {
      AssetGenerator.drawDirtTile(ctx, (4 + i) * size, 0, i);
    }

    // Row 1: Water (8-11), Sand (12-15)
    for (let i = 0; i < 4; i++) {
      AssetGenerator.drawWaterTile(ctx, i * size, size, i);
    }
    for (let i = 0; i < 4; i++) {
      AssetGenerator.drawSandTile(ctx, (4 + i) * size, size, i);
    }

    // Row 2: Stone path (16-19), Flowers (20-23)
    for (let i = 0; i < 4; i++) {
      AssetGenerator.drawStonePath(ctx, i * size, 2 * size, i);
    }
    for (let i = 0; i < 4; i++) {
      AssetGenerator.drawFlowerTile(ctx, (4 + i) * size, 2 * size, i);
    }

    // Row 3: Farm soil (24-27), Fence (28-31)
    AssetGenerator.drawFarmSoil(ctx, 0, 3 * size, 'dry');
    AssetGenerator.drawFarmSoil(ctx, size, 3 * size, 'wet');
    AssetGenerator.drawFarmSoil(ctx, 2 * size, 3 * size, 'tilled');
    AssetGenerator.drawFarmSoil(ctx, 3 * size, 3 * size, 'planted');
    AssetGenerator.drawFence(ctx, 4 * size, 3 * size, 'h');
    AssetGenerator.drawFence(ctx, 5 * size, 3 * size, 'v');
    AssetGenerator.drawFence(ctx, 6 * size, 3 * size, 'tl');
    AssetGenerator.drawFence(ctx, 7 * size, 3 * size, 'tr');

    // Row 4: Dark forest (32-35), Marsh (36-39)
    for (let i = 0; i < 4; i++) {
      AssetGenerator.drawDarkForestTile(ctx, i * size, 4 * size, i);
    }
    for (let i = 0; i < 4; i++) {
      AssetGenerator.drawMarshTile(ctx, (4 + i) * size, 4 * size, i);
    }

    // Row 5: Rocky ground (40-43), Meadow (44-47)
    for (let i = 0; i < 4; i++) {
      AssetGenerator.drawRockyTile(ctx, i * size, 5 * size, i);
    }
    for (let i = 0; i < 4; i++) {
      AssetGenerator.drawMeadowTile(ctx, (4 + i) * size, 5 * size, i);
    }

    canvas.refresh();

    const texture = scene.textures.get('terrain');
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        texture.add(row * cols + col, 0, col * size, row * size, size, size);
      }
    }
  }

  private static drawGrassTile(ctx: Ctx, ox: number, oy: number, variant: number): void {
    // Base grass
    rect(ctx, ox, oy, 16, 16, C.grass);

    // Subtle variations
    const seed = variant * 37;
    for (let i = 0; i < 8; i++) {
      const x = ((seed + i * 17) % 14) + 1;
      const y = ((seed + i * 23) % 14) + 1;
      px(ctx, ox + x, oy + y, i % 2 === 0 ? C.grassLight : C.grassDark);
    }

    // Grass blades
    if (variant < 2) {
      px(ctx, ox + 3, oy + 5, C.grassLight);
      px(ctx, ox + 3, oy + 4, C.grassLight);
      px(ctx, ox + 10, oy + 9, C.grassLight);
      px(ctx, ox + 10, oy + 8, C.grassLight);
    }
    if (variant === 2 || variant === 3) {
      px(ctx, ox + 7, oy + 3, C.leafDark);
      px(ctx, ox + 12, oy + 11, C.leafDark);
    }
  }

  private static drawDirtTile(ctx: Ctx, ox: number, oy: number, variant: number): void {
    rect(ctx, ox, oy, 16, 16, C.dirt);

    const seed = variant * 41;
    for (let i = 0; i < 6; i++) {
      const x = ((seed + i * 19) % 14) + 1;
      const y = ((seed + i * 29) % 14) + 1;
      px(ctx, ox + x, oy + y, i % 2 === 0 ? C.dirtLight : C.wood);
    }

    // Small pebbles
    if (variant >= 2) {
      px(ctx, ox + 5, oy + 12, C.gray);
      px(ctx, ox + 11, oy + 4, C.gray);
    }
  }

  private static drawWaterTile(ctx: Ctx, ox: number, oy: number, frame: number): void {
    rect(ctx, ox, oy, 16, 16, C.water);

    // Animated wave pattern
    const offset = frame * 2;
    for (let y = 0; y < 16; y += 4) {
      for (let x = 0; x < 16; x += 3) {
        const wx = (x + offset + y) % 16;
        px(ctx, ox + wx, oy + y, C.waterLight);
        if (wx + 1 < 16) {
          px(ctx, ox + wx + 1, oy + y, C.waterDark);
        }
      }
    }
  }

  private static drawSandTile(ctx: Ctx, ox: number, oy: number, variant: number): void {
    rect(ctx, ox, oy, 16, 16, '#d4b86a');

    const seed = variant * 31;
    for (let i = 0; i < 5; i++) {
      const x = ((seed + i * 13) % 14) + 1;
      const y = ((seed + i * 21) % 14) + 1;
      px(ctx, ox + x, oy + y, '#c4a85a');
    }
    for (let i = 0; i < 3; i++) {
      const x = ((seed + i * 37) % 14) + 1;
      const y = ((seed + i * 43) % 14) + 1;
      px(ctx, ox + x, oy + y, '#e4c87a');
    }
  }

  private static drawStonePath(ctx: Ctx, ox: number, oy: number, variant: number): void {
    rect(ctx, ox, oy, 16, 16, C.dirt);

    // Stone blocks
    ctx.fillStyle = '#999999';
    if (variant % 2 === 0) {
      rect(ctx, ox + 1, oy + 1, 6, 6, '#aaaaaa');
      rect(ctx, ox + 8, oy + 1, 7, 6, '#999999');
      rect(ctx, ox + 1, oy + 8, 7, 7, '#999999');
      rect(ctx, ox + 9, oy + 8, 6, 7, '#aaaaaa');
    } else {
      rect(ctx, ox + 1, oy + 1, 7, 7, '#999999');
      rect(ctx, ox + 9, oy + 1, 6, 6, '#aaaaaa');
      rect(ctx, ox + 1, oy + 9, 6, 6, '#aaaaaa');
      rect(ctx, ox + 8, oy + 8, 7, 7, '#999999');
    }

    // Mortar lines
    ctx.fillStyle = '#777777';
    rect(ctx, ox, oy + 7, 16, 1, '#777777');
    rect(ctx, ox + 7, oy, 1, 16, '#777777');
  }

  private static drawFlowerTile(ctx: Ctx, ox: number, oy: number, variant: number): void {
    // Base grass
    rect(ctx, ox, oy, 16, 16, C.grass);

    const flowers = [
      { cx: 4, cy: 4, color: '#ff6666' },
      { cx: 11, cy: 6, color: '#ffff66' },
      { cx: 7, cy: 12, color: '#ff66ff' },
      { cx: 13, cy: 13, color: '#6666ff' },
    ];

    // Draw 1-3 flowers depending on variant
    const count = (variant % 3) + 1;
    for (let i = 0; i < count; i++) {
      const f = flowers[(variant + i) % flowers.length];
      px(ctx, ox + f.cx, oy + f.cy, f.color);
      px(ctx, ox + f.cx - 1, oy + f.cy, f.color);
      px(ctx, ox + f.cx + 1, oy + f.cy, f.color);
      px(ctx, ox + f.cx, oy + f.cy - 1, f.color);
      px(ctx, ox + f.cx, oy + f.cy + 1, C.sunYellow);
      // Stem
      px(ctx, ox + f.cx, oy + f.cy + 2, C.grassDark);
      px(ctx, ox + f.cx, oy + f.cy + 3, C.grassDark);
    }
  }

  private static drawDarkForestTile(ctx: Ctx, ox: number, oy: number, variant: number): void {
    // Dark humus ground
    rect(ctx, ox, oy, 16, 16, '#2a3a20');
    const seed = variant * 43;
    for (let i = 0; i < 10; i++) {
      const x = ((seed + i * 17) % 14) + 1;
      const y = ((seed + i * 23) % 14) + 1;
      px(ctx, ox + x, oy + y, i % 3 === 0 ? '#1a2a14' : '#3a4a2a');
    }
    // Fallen leaves / twigs
    if (variant < 2) {
      px(ctx, ox + 4, oy + 10, '#5a4a20');
      px(ctx, ox + 5, oy + 10, '#5a4a20');
      px(ctx, ox + 11, oy + 5, '#4a3a18');
    }
    if (variant >= 2) {
      // Mushroom dots
      px(ctx, ox + 3, oy + 12, '#cc8844');
      px(ctx, ox + 3, oy + 11, '#ddaa66');
      px(ctx, ox + 10, oy + 7, '#cc8844');
      px(ctx, ox + 10, oy + 6, '#ddaa66');
    }
    // Roots snaking across
    if (variant === 1 || variant === 3) {
      rect(ctx, ox + 2, oy + 14, 5, 1, '#4a3a18');
      rect(ctx, ox + 9, oy + 3, 4, 1, '#4a3a18');
    }
  }

  private static drawMarshTile(ctx: Ctx, ox: number, oy: number, variant: number): void {
    // Muddy green-brown base
    rect(ctx, ox, oy, 16, 16, '#4a6a3a');
    const seed = variant * 53;
    // Mud patches
    for (let i = 0; i < 6; i++) {
      const x = ((seed + i * 19) % 12) + 2;
      const y = ((seed + i * 31) % 12) + 2;
      rect(ctx, ox + x, oy + y, 2, 2, '#5a5430');
    }
    // Shallow water puddles
    const pudX = ((seed * 7) % 8) + 2;
    const pudY = ((seed * 11) % 8) + 4;
    rect(ctx, ox + pudX, oy + pudY, 4, 3, '#5a8a6a');
    px(ctx, ox + pudX + 1, oy + pudY, '#7aaa8a');
    // Reeds
    if (variant % 2 === 0) {
      px(ctx, ox + pudX - 1, oy + pudY - 1, '#6a8a3a');
      px(ctx, ox + pudX - 1, oy + pudY - 2, '#6a8a3a');
      px(ctx, ox + pudX - 1, oy + pudY - 3, '#5a7a2a');
    }
    if (variant >= 2) {
      px(ctx, ox + 12, oy + 3, '#6a8a3a');
      px(ctx, ox + 12, oy + 2, '#5a7a2a');
    }
  }

  private static drawRockyTile(ctx: Ctx, ox: number, oy: number, variant: number): void {
    // Grey-brown rocky ground
    rect(ctx, ox, oy, 16, 16, '#8a8070');
    const seed = variant * 61;
    for (let i = 0; i < 8; i++) {
      const x = ((seed + i * 13) % 14) + 1;
      const y = ((seed + i * 29) % 14) + 1;
      px(ctx, ox + x, oy + y, i % 2 === 0 ? '#9a9080' : '#7a7060');
    }
    // Small rocks
    if (variant < 2) {
      rect(ctx, ox + 3, oy + 5, 3, 2, '#6a6458');
      rect(ctx, ox + 4, oy + 4, 2, 1, '#7a7468');
    }
    if (variant >= 1) {
      rect(ctx, ox + 10, oy + 10, 4, 3, '#6a6458');
      rect(ctx, ox + 11, oy + 9, 2, 1, '#7a7468');
    }
    // Cracks
    if (variant === 3) {
      px(ctx, ox + 7, oy + 7, '#5a5448');
      px(ctx, ox + 8, oy + 8, '#5a5448');
      px(ctx, ox + 8, oy + 9, '#5a5448');
    }
    // Sparse moss
    if (variant === 0) {
      px(ctx, ox + 12, oy + 3, '#6a8a5a');
      px(ctx, ox + 13, oy + 3, '#6a8a5a');
    }
  }

  private static drawMeadowTile(ctx: Ctx, ox: number, oy: number, variant: number): void {
    // Lush bright grass base
    rect(ctx, ox, oy, 16, 16, '#5aaa4a');
    const seed = variant * 37;
    for (let i = 0; i < 6; i++) {
      const x = ((seed + i * 17) % 14) + 1;
      const y = ((seed + i * 23) % 14) + 1;
      px(ctx, ox + x, oy + y, i % 2 === 0 ? '#6abb5a' : '#4a9a3a');
    }
    // Tall grass tufts
    const colors = ['#7acc6a', '#5abb4a'];
    for (let i = 0; i < 3; i++) {
      const gx = ((seed + i * 41) % 12) + 2;
      const gy = ((seed + i * 59) % 10) + 3;
      px(ctx, ox + gx, oy + gy, colors[i % 2]);
      px(ctx, ox + gx, oy + gy - 1, colors[i % 2]);
      px(ctx, ox + gx, oy + gy - 2, colors[(i + 1) % 2]);
    }
    // Wildflowers (variant-based)
    const flowerColors = ['#ff7766', '#ffdd44', '#aa88ee', '#66bbff'];
    const fc = flowerColors[variant];
    const fx = ((seed * 7) % 10) + 3;
    const fy = ((seed * 13) % 8) + 4;
    px(ctx, ox + fx, oy + fy, fc);
    px(ctx, ox + fx - 1, oy + fy, fc);
    px(ctx, ox + fx + 1, oy + fy, fc);
    px(ctx, ox + fx, oy + fy - 1, fc);
    px(ctx, ox + fx, oy + fy + 1, '#5aaa4a'); // center dot matches grass
    // Butterfly on some variants
    if (variant === 1 || variant === 3) {
      px(ctx, ox + 12, oy + 2, '#ffaacc');
      px(ctx, ox + 14, oy + 2, '#ffaacc');
      px(ctx, ox + 13, oy + 3, '#ffffff');
    }
  }

  private static drawFarmSoil(ctx: Ctx, ox: number, oy: number, type: string): void {
    const baseColor = type === 'wet' ? '#6b4f23' : type === 'tilled' ? '#7a5a28' : '#8b6914';
    rect(ctx, ox, oy, 16, 16, baseColor);

    if (type === 'tilled' || type === 'planted') {
      // Furrow lines
      for (let y = 2; y < 16; y += 4) {
        rect(ctx, ox + 1, oy + y, 14, 1, '#5a3a10');
        rect(ctx, ox + 1, oy + y + 1, 14, 1, '#a5824a');
      }
    }

    if (type === 'wet') {
      // Water sheen
      for (let i = 0; i < 4; i++) {
        px(ctx, ox + 3 + i * 3, oy + 5, '#8899bb');
        px(ctx, ox + 4 + i * 3, oy + 11, '#8899bb');
      }
    }

    if (type === 'planted') {
      // Tiny seed marker
      px(ctx, ox + 8, oy + 7, C.grassLight);
      px(ctx, ox + 8, oy + 8, C.dirt);
    }
  }

  private static drawFence(ctx: Ctx, ox: number, oy: number, type: string): void {
    // Base is transparent (grass underneath)
    rect(ctx, ox, oy, 16, 16, C.grass);

    ctx.fillStyle = C.wood;

    if (type === 'h') {
      // Horizontal fence
      rect(ctx, ox, oy + 5, 16, 2, C.wood);
      rect(ctx, ox, oy + 10, 16, 2, C.wood);
      // Posts at edges
      rect(ctx, ox + 1, oy + 3, 2, 11, C.woodLight);
      rect(ctx, ox + 13, oy + 3, 2, 11, C.woodLight);
    } else if (type === 'v') {
      // Vertical fence
      rect(ctx, ox + 6, oy, 3, 16, C.wood);
      rect(ctx, ox + 5, oy + 2, 1, 4, C.woodLight);
      rect(ctx, ox + 5, oy + 10, 1, 4, C.woodLight);
      rect(ctx, ox + 10, oy + 2, 1, 4, C.woodLight);
      rect(ctx, ox + 10, oy + 10, 1, 4, C.woodLight);
    } else if (type === 'tl') {
      // Corner top-left
      rect(ctx, ox + 6, oy + 6, 10, 2, C.wood);
      rect(ctx, ox + 6, oy + 6, 2, 10, C.wood);
      rect(ctx, ox + 5, oy + 5, 3, 3, C.woodLight);
    } else {
      // Corner top-right
      rect(ctx, ox, oy + 6, 10, 2, C.wood);
      rect(ctx, ox + 8, oy + 6, 2, 10, C.wood);
      rect(ctx, ox + 7, oy + 5, 3, 3, C.woodLight);
    }
  }

  // =========================================================================
  // OBJECTS: 4 cols x 4 rows = 16 objects (16x16 each)
  // =========================================================================
  static generateObjects(scene: Phaser.Scene): void {
    const cols = 4, rows = 4, size = 16;
    const canvas = scene.textures.createCanvas('objects', cols * size, rows * size)!;
    const ctx = canvas.context;

    const drawers = [
      // Row 0
      AssetGenerator.drawWell,
      AssetGenerator.drawBarn,
      AssetGenerator.drawNursery,
      AssetGenerator.drawScarecrow,
      // Row 1
      AssetGenerator.drawChest,
      AssetGenerator.drawWateringCan,
      AssetGenerator.drawHoe,
      AssetGenerator.drawSeedBag,
      // Row 2
      AssetGenerator.drawCampfire,
      AssetGenerator.drawTent,
      AssetGenerator.drawSignPost,
      AssetGenerator.drawMailbox,
      // Row 3
      AssetGenerator.drawTreeStump,
      AssetGenerator.drawRockSmall,
      AssetGenerator.drawRockLarge,
      AssetGenerator.drawBush,
    ];

    drawers.forEach((draw, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      draw(ctx, col * size, row * size);
    });

    canvas.refresh();

    const texture = scene.textures.get('objects');
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        texture.add(row * cols + col, 0, col * size, row * size, size, size);
      }
    }
  }

  private static drawWell(ctx: Ctx, ox: number, oy: number): void {
    // Stone base
    rect(ctx, ox + 2, oy + 8, 12, 7, '#888888');
    rect(ctx, ox + 3, oy + 9, 10, 5, '#666666');
    // Water inside
    rect(ctx, ox + 4, oy + 10, 8, 3, C.water);
    // Roof
    rect(ctx, ox + 1, oy + 3, 14, 2, C.wood);
    rect(ctx, ox + 3, oy + 2, 10, 1, C.woodLight);
    // Posts
    rect(ctx, ox + 3, oy + 4, 1, 5, C.wood);
    rect(ctx, ox + 12, oy + 4, 1, 5, C.wood);
    // Bucket
    px(ctx, ox + 8, oy + 5, C.gray);
    px(ctx, ox + 8, oy + 6, C.gray);
  }

  private static drawBarn(ctx: Ctx, ox: number, oy: number): void {
    // Walls
    rect(ctx, ox + 1, oy + 5, 14, 10, C.wood);
    // Roof
    rect(ctx, ox, oy + 3, 16, 3, C.red);
    rect(ctx, ox + 2, oy + 2, 12, 1, C.redDark);
    rect(ctx, ox + 4, oy + 1, 8, 1, C.redDark);
    // Door
    rect(ctx, ox + 6, oy + 10, 4, 5, C.bark);
    px(ctx, ox + 9, oy + 12, C.gold);
    // Window
    rect(ctx, ox + 3, oy + 7, 2, 2, C.skyBlue);
    rect(ctx, ox + 11, oy + 7, 2, 2, C.skyBlue);
  }

  private static drawNursery(ctx: Ctx, ox: number, oy: number): void {
    // Greenhouse frame
    rect(ctx, ox + 1, oy + 5, 14, 10, '#aaccaa');
    // Glass roof
    rect(ctx, ox + 2, oy + 2, 12, 4, '#88ccff');
    rect(ctx, ox + 3, oy + 1, 10, 1, '#99ddff');
    // Frame lines
    rect(ctx, ox + 7, oy + 2, 1, 4, C.white);
    // Plants inside
    px(ctx, ox + 4, oy + 10, C.grassLight);
    px(ctx, ox + 7, oy + 9, C.leaf);
    px(ctx, ox + 10, oy + 10, C.grassLight);
    px(ctx, ox + 12, oy + 11, C.sunYellow);
  }

  private static drawScarecrow(ctx: Ctx, ox: number, oy: number): void {
    // Post
    rect(ctx, ox + 7, oy + 5, 2, 10, C.wood);
    // Arms
    rect(ctx, ox + 3, oy + 7, 10, 1, C.wood);
    // Head
    rect(ctx, ox + 5, oy + 1, 6, 5, '#ddbb88');
    // Hat
    rect(ctx, ox + 4, oy + 0, 8, 2, C.hat);
    rect(ctx, ox + 3, oy + 2, 10, 1, C.hatBand);
    // Face
    px(ctx, ox + 6, oy + 3, C.black);
    px(ctx, ox + 9, oy + 3, C.black);
    px(ctx, ox + 7, oy + 4, C.orange);
    // Cloth on arms
    rect(ctx, ox + 3, oy + 6, 2, 3, C.shirt);
    rect(ctx, ox + 11, oy + 6, 2, 3, C.shirt);
  }

  private static drawChest(ctx: Ctx, ox: number, oy: number): void {
    rect(ctx, ox + 2, oy + 6, 12, 8, C.wood);
    rect(ctx, ox + 2, oy + 4, 12, 3, C.woodLight);
    // Lock
    px(ctx, ox + 7, oy + 8, C.gold);
    px(ctx, ox + 8, oy + 8, C.gold);
    // Highlights
    rect(ctx, ox + 2, oy + 6, 12, 1, '#5a4020');
  }

  private static drawWateringCan(ctx: Ctx, ox: number, oy: number): void {
    // Body
    rect(ctx, ox + 3, oy + 7, 8, 6, '#7799bb');
    rect(ctx, ox + 4, oy + 6, 6, 1, '#8899bb');
    // Spout
    rect(ctx, ox + 10, oy + 5, 3, 2, '#7799bb');
    px(ctx, ox + 13, oy + 5, '#6688aa');
    // Handle
    rect(ctx, ox + 5, oy + 4, 4, 1, '#6688aa');
    px(ctx, ox + 4, oy + 5, '#6688aa');
    px(ctx, ox + 9, oy + 5, '#6688aa');
    // Water drops
    px(ctx, ox + 13, oy + 7, C.waterLight);
    px(ctx, ox + 12, oy + 8, C.waterLight);
  }

  private static drawHoe(ctx: Ctx, ox: number, oy: number): void {
    // Handle (diagonal)
    for (let i = 0; i < 10; i++) {
      px(ctx, ox + 4 + Math.floor(i * 0.7), oy + 3 + i, C.wood);
    }
    // Blade
    rect(ctx, ox + 2, oy + 12, 5, 2, '#aaaaaa');
    rect(ctx, ox + 2, oy + 14, 5, 1, '#888888');
  }

  private static drawSeedBag(ctx: Ctx, ox: number, oy: number): void {
    // Bag body
    rect(ctx, ox + 3, oy + 6, 10, 8, '#ccaa77');
    rect(ctx, ox + 4, oy + 5, 8, 1, '#bbaa66');
    // Opening
    rect(ctx, ox + 5, oy + 4, 6, 2, '#ddbb88');
    // Seeds peeking out
    px(ctx, ox + 6, oy + 5, C.grassLight);
    px(ctx, ox + 8, oy + 4, C.sunYellow);
    px(ctx, ox + 10, oy + 5, C.red);
    // Label
    rect(ctx, ox + 5, oy + 9, 6, 3, '#ffffff');
    px(ctx, ox + 7, oy + 10, C.grassLight);
  }

  private static drawCampfire(ctx: Ctx, ox: number, oy: number): void {
    // Stones
    const stones = [[3,12],[5,13],[7,14],[9,13],[11,12],[4,11],[10,11]];
    stones.forEach(([x,y]) => px(ctx, ox+x, oy+y, '#888888'));
    // Logs
    rect(ctx, ox + 5, oy + 11, 6, 2, C.wood);
    // Flames
    rect(ctx, ox + 6, oy + 7, 4, 4, C.firePepperOrange);
    rect(ctx, ox + 7, oy + 5, 2, 2, C.sunYellow);
    px(ctx, ox + 6, oy + 6, C.firePepperRed);
    px(ctx, ox + 9, oy + 6, C.firePepperRed);
    px(ctx, ox + 8, oy + 4, C.sunYellow);
  }

  private static drawTent(ctx: Ctx, ox: number, oy: number): void {
    // Tent body (triangle)
    for (let row = 0; row < 10; row++) {
      const halfWidth = Math.floor(row * 0.8) + 1;
      rect(ctx, ox + 8 - halfWidth, oy + 4 + row, halfWidth * 2, 1, '#cc8844');
    }
    // Opening
    rect(ctx, ox + 6, oy + 10, 4, 4, '#443322');
    // Pole top
    px(ctx, ox + 8, oy + 2, C.wood);
    px(ctx, ox + 8, oy + 3, C.wood);
  }

  private static drawSignPost(ctx: Ctx, ox: number, oy: number): void {
    // Post
    rect(ctx, ox + 7, oy + 6, 2, 9, C.wood);
    // Sign board
    rect(ctx, ox + 2, oy + 3, 12, 5, C.woodLight);
    rect(ctx, ox + 2, oy + 3, 12, 1, C.wood);
    rect(ctx, ox + 2, oy + 7, 12, 1, C.wood);
    // Text dots
    px(ctx, ox + 4, oy + 5, C.black);
    px(ctx, ox + 6, oy + 5, C.black);
    px(ctx, ox + 8, oy + 5, C.black);
    px(ctx, ox + 10, oy + 5, C.black);
  }

  private static drawMailbox(ctx: Ctx, ox: number, oy: number): void {
    // Post
    rect(ctx, ox + 7, oy + 8, 2, 7, C.wood);
    // Box
    rect(ctx, ox + 3, oy + 4, 10, 5, '#6688aa');
    rect(ctx, ox + 3, oy + 3, 10, 1, '#7799bb');
    // Flag
    rect(ctx, ox + 12, oy + 3, 2, 1, C.red);
    rect(ctx, ox + 13, oy + 4, 1, 2, C.red);
    // Slot
    rect(ctx, ox + 5, oy + 6, 6, 1, '#334455');
  }

  private static drawTreeStump(ctx: Ctx, ox: number, oy: number): void {
    rect(ctx, ox + 3, oy + 8, 10, 7, C.wood);
    rect(ctx, ox + 4, oy + 7, 8, 1, C.woodLight);
    // Rings
    rect(ctx, ox + 5, oy + 10, 6, 4, C.dirtLight);
    rect(ctx, ox + 6, oy + 11, 4, 2, C.wood);
    px(ctx, ox + 7, oy + 11, C.dirtLight);
  }

  private static drawRockSmall(ctx: Ctx, ox: number, oy: number): void {
    rect(ctx, ox + 5, oy + 10, 6, 4, '#999999');
    rect(ctx, ox + 6, oy + 9, 4, 1, '#aaaaaa');
    px(ctx, ox + 7, oy + 10, '#bbbbbb'); // highlight
  }

  private static drawRockLarge(ctx: Ctx, ox: number, oy: number): void {
    rect(ctx, ox + 3, oy + 7, 10, 8, '#888888');
    rect(ctx, ox + 4, oy + 6, 8, 1, '#999999');
    rect(ctx, ox + 5, oy + 5, 6, 1, '#aaaaaa');
    px(ctx, ox + 6, oy + 7, '#aaaaaa');
    px(ctx, ox + 10, oy + 9, '#777777');
  }

  private static drawBush(ctx: Ctx, ox: number, oy: number): void {
    rect(ctx, ox + 2, oy + 7, 12, 8, C.grassDark);
    rect(ctx, ox + 3, oy + 5, 10, 3, C.grass);
    rect(ctx, ox + 5, oy + 4, 6, 2, C.grassLight);
    // Berry highlights
    px(ctx, ox + 4, oy + 8, C.red);
    px(ctx, ox + 9, oy + 7, C.red);
    px(ctx, ox + 11, oy + 10, C.red);
  }

  // =========================================================================
  // UI ELEMENTS
  // =========================================================================
  static generateUI(scene: Phaser.Scene): void {
    // UI elements texture - simple textures for HUD
    const canvas = scene.textures.createCanvas('ui-elements', 128, 32)!;
    const ctx = canvas.context;

    // Inventory slot (32x32 at 0,0)
    rect(ctx, 0, 0, 32, 32, '#333333');
    ctx.strokeStyle = '#666666';
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, 31, 31);

    // Sun icon (16x16 at 32,0)
    rect(ctx, 36, 4, 8, 8, C.sunYellow);
    px(ctx, 40, 2, C.sunOrange);
    px(ctx, 40, 13, C.sunOrange);
    px(ctx, 34, 8, C.sunOrange);
    px(ctx, 46, 8, C.sunOrange);
    px(ctx, 35, 4, C.sunOrange);
    px(ctx, 45, 4, C.sunOrange);
    px(ctx, 35, 12, C.sunOrange);
    px(ctx, 45, 12, C.sunOrange);

    // Moon icon (16x16 at 48,0)
    rect(ctx, 52, 3, 8, 10, C.moonPurple);
    rect(ctx, 54, 2, 6, 1, C.moonBlue);
    rect(ctx, 54, 13, 6, 1, C.moonBlue);
    rect(ctx, 55, 4, 5, 6, C.nightDark); // Crescent cutout

    canvas.refresh();

    const texture = scene.textures.get('ui-elements');
    texture.add(0, 0, 0, 0, 32, 32); // Inventory slot
    texture.add(1, 0, 32, 0, 16, 16); // Sun icon
    texture.add(2, 0, 48, 0, 16, 16); // Moon icon
  }

  // =========================================================================
  // MONSTERS: 5 rows x 4 cols = 20 frames (16x16 each)
  // Each row = monster type, each col = walk frame
  // =========================================================================
  static generateMonsters(scene: Phaser.Scene): void {
    const cols = 4, rows = 5, size = 16;
    const canvas = scene.textures.createCanvas('monsters', cols * size, rows * size)!;
    const ctx = canvas.context;

    const drawers = [
      AssetGenerator.drawPiranhaPlant,
      AssetGenerator.drawThornCrawler,
      AssetGenerator.drawFungalBrute,
      AssetGenerator.drawWiltWraith,
      AssetGenerator.drawRootTitan,
    ];

    for (let row = 0; row < rows; row++) {
      for (let frame = 0; frame < cols; frame++) {
        drawers[row](ctx, frame * size, row * size, frame);
      }
    }

    canvas.refresh();
    const texture = scene.textures.get('monsters');
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        texture.add(row * cols + col, 0, col * size, row * size, size, size);
      }
    }
  }

  // Piranha Plant - walking venus flytrap with teeth
  private static drawPiranhaPlant(ctx: Ctx, ox: number, oy: number, frame: number): void {
    const bob = frame % 2 === 0 ? 0 : -1;
    // Stem/legs
    rect(ctx, ox + 6, oy + 10 + bob, 2, 5, '#2a5a1a');
    rect(ctx, ox + 9, oy + 10 + bob, 2, 5, '#2a5a1a');
    // Body
    rect(ctx, ox + 4, oy + 6 + bob, 8, 5, '#3a8a2a');
    // Head/jaw
    rect(ctx, ox + 3, oy + 2 + bob, 10, 5, '#44aa33');
    rect(ctx, ox + 4, oy + 1 + bob, 8, 1, '#3a8a2a');
    // Teeth (top jaw)
    px(ctx, ox + 4, oy + 6 + bob, '#ffffff');
    px(ctx, ox + 6, oy + 6 + bob, '#ffffff');
    px(ctx, ox + 8, oy + 6 + bob, '#ffffff');
    px(ctx, ox + 10, oy + 6 + bob, '#ffffff');
    // Teeth (bottom jaw)
    px(ctx, ox + 5, oy + 7 + bob, '#ffffff');
    px(ctx, ox + 7, oy + 7 + bob, '#ffffff');
    px(ctx, ox + 9, oy + 7 + bob, '#ffffff');
    // Angry eyes
    px(ctx, ox + 5, oy + 3 + bob, '#ff2222');
    px(ctx, ox + 9, oy + 3 + bob, '#ff2222');
    // Mouth gap (red inside)
    rect(ctx, ox + 5, oy + 6 + bob, 6, 2, '#880000');
    // Re-draw teeth over mouth
    px(ctx, ox + 5, oy + 6 + bob, '#ffffff');
    px(ctx, ox + 7, oy + 6 + bob, '#ffffff');
    px(ctx, ox + 9, oy + 6 + bob, '#ffffff');
    // Leaf details
    if (frame === 1 || frame === 3) {
      px(ctx, ox + 3, oy + 8 + bob, '#55cc44');
      px(ctx, ox + 12, oy + 8 + bob, '#55cc44');
    }
  }

  // Thorn Crawler - spiky low bug
  private static drawThornCrawler(ctx: Ctx, ox: number, oy: number, frame: number): void {
    const bob = frame % 2 === 0 ? 0 : 1;
    // Body (low to ground)
    rect(ctx, ox + 3, oy + 9 + bob, 10, 4, '#5a3a1a');
    rect(ctx, ox + 4, oy + 8 + bob, 8, 1, '#6a4a2a');
    // Thorns on top
    px(ctx, ox + 4, oy + 7 + bob, '#8a5a2a');
    px(ctx, ox + 6, oy + 6 + bob, '#8a5a2a');
    px(ctx, ox + 8, oy + 7 + bob, '#8a5a2a');
    px(ctx, ox + 10, oy + 6 + bob, '#8a5a2a');
    // Legs
    const legOffset = frame % 2;
    rect(ctx, ox + 3 + legOffset, oy + 13 + bob, 1, 2, '#4a2a0a');
    rect(ctx, ox + 6, oy + 13 + bob, 1, 2, '#4a2a0a');
    rect(ctx, ox + 9, oy + 13 + bob, 1, 2, '#4a2a0a');
    rect(ctx, ox + 12 - legOffset, oy + 13 + bob, 1, 2, '#4a2a0a');
    // Eyes
    px(ctx, ox + 5, oy + 10 + bob, '#ff4444');
    px(ctx, ox + 9, oy + 10 + bob, '#ff4444');
  }

  // Fungal Brute - big mushroom beast
  private static drawFungalBrute(ctx: Ctx, ox: number, oy: number, frame: number): void {
    const bob = frame % 2 === 0 ? 0 : -1;
    // Thick legs
    rect(ctx, ox + 4, oy + 11 + bob, 3, 4, '#6a5a4a');
    rect(ctx, ox + 9, oy + 11 + bob, 3, 4, '#6a5a4a');
    // Body (wide, mushroom stalk)
    rect(ctx, ox + 3, oy + 6 + bob, 10, 6, '#8a7a6a');
    // Mushroom cap
    rect(ctx, ox + 1, oy + 2 + bob, 14, 5, '#cc4444');
    rect(ctx, ox + 2, oy + 1 + bob, 12, 1, '#aa3333');
    rect(ctx, ox + 4, oy + 0 + bob, 8, 1, '#993333');
    // Spots on cap
    px(ctx, ox + 4, oy + 3 + bob, '#ffcccc');
    px(ctx, ox + 8, oy + 2 + bob, '#ffcccc');
    px(ctx, ox + 11, oy + 4 + bob, '#ffcccc');
    // Eyes
    rect(ctx, ox + 5, oy + 7 + bob, 2, 2, '#ffffff');
    rect(ctx, ox + 9, oy + 7 + bob, 2, 2, '#ffffff');
    px(ctx, ox + 6, oy + 8 + bob, '#000000');
    px(ctx, ox + 10, oy + 8 + bob, '#000000');
    // Mouth
    rect(ctx, ox + 6, oy + 10 + bob, 4, 1, '#553322');
  }

  // Wilt Wraith - ghostly transparent plant spirit
  private static drawWiltWraith(ctx: Ctx, ox: number, oy: number, frame: number): void {
    const sway = frame % 2 === 0 ? 0 : 1;
    // Wispy body (ghostly)
    rect(ctx, ox + 5 + sway, oy + 4, 6, 8, '#667788');
    rect(ctx, ox + 4 + sway, oy + 6, 8, 4, '#556677');
    // Tattered bottom
    px(ctx, ox + 4 + sway, oy + 12, '#556677');
    px(ctx, ox + 6 + sway, oy + 13, '#445566');
    px(ctx, ox + 8 + sway, oy + 12, '#556677');
    px(ctx, ox + 10 + sway, oy + 14, '#445566');
    px(ctx, ox + 5 + sway, oy + 14, '#445566');
    // Dead flower/head
    rect(ctx, ox + 5 + sway, oy + 1, 6, 4, '#887766');
    rect(ctx, ox + 6 + sway, oy + 0, 4, 1, '#776655');
    // Hollow eyes (glowing)
    px(ctx, ox + 6 + sway, oy + 2, '#aaffaa');
    px(ctx, ox + 9 + sway, oy + 2, '#aaffaa');
    // Wilted petals
    px(ctx, ox + 4 + sway, oy + 2, '#665544');
    px(ctx, ox + 11 + sway, oy + 3, '#665544');
  }

  // Root Titan - massive boss made of roots
  private static drawRootTitan(ctx: Ctx, ox: number, oy: number, frame: number): void {
    const bob = frame % 2 === 0 ? 0 : -1;
    // Massive root legs
    rect(ctx, ox + 2, oy + 10 + bob, 4, 5, '#5a3a1a');
    rect(ctx, ox + 10, oy + 10 + bob, 4, 5, '#5a3a1a');
    // Root tendrils at feet
    px(ctx, ox + 1, oy + 14 + bob, '#4a2a0a');
    px(ctx, ox + 6, oy + 15, '#4a2a0a');
    px(ctx, ox + 14, oy + 14 + bob, '#4a2a0a');
    // Massive body
    rect(ctx, ox + 1, oy + 4 + bob, 14, 7, '#6a4a2a');
    rect(ctx, ox + 2, oy + 3 + bob, 12, 1, '#7a5a3a');
    // Bark texture
    px(ctx, ox + 4, oy + 5 + bob, '#5a3a1a');
    px(ctx, ox + 8, oy + 6 + bob, '#5a3a1a');
    px(ctx, ox + 11, oy + 8 + bob, '#5a3a1a');
    px(ctx, ox + 3, oy + 9 + bob, '#5a3a1a');
    // Head / crown of roots
    rect(ctx, ox + 3, oy + 1 + bob, 10, 3, '#7a5a3a');
    rect(ctx, ox + 5, oy + 0 + bob, 6, 1, '#8a6a4a');
    // Branches from head
    px(ctx, ox + 2, oy + 0 + bob, '#6a4a2a');
    px(ctx, ox + 13, oy + 0 + bob, '#6a4a2a');
    px(ctx, ox + 1, oy + 1 + bob, '#5a3a1a');
    px(ctx, ox + 14, oy + 1 + bob, '#5a3a1a');
    // Glowing eyes
    rect(ctx, ox + 5, oy + 2 + bob, 2, 2, '#ff4400');
    rect(ctx, ox + 9, oy + 2 + bob, 2, 2, '#ff4400');
    px(ctx, ox + 5, oy + 2 + bob, '#ffaa00');
    px(ctx, ox + 9, oy + 2 + bob, '#ffaa00');
    // Mouth
    rect(ctx, ox + 6, oy + 5 + bob, 4, 2, '#330000');
    px(ctx, ox + 7, oy + 5 + bob, '#ff2200');
  }

  // =========================================================================
  // WEAPONS: 4 cols x 1 row = 4 frames (16x16 each)
  // Sword swing frames
  // =========================================================================
  static generateWeapons(scene: Phaser.Scene): void {
    const cols = 4, size = 16;
    const canvas = scene.textures.createCanvas('weapons', cols * size, size)!;
    const ctx = canvas.context;

    for (let i = 0; i < cols; i++) {
      AssetGenerator.drawSword(ctx, i * size, 0, i);
    }

    canvas.refresh();
    const texture = scene.textures.get('weapons');
    for (let col = 0; col < cols; col++) {
      texture.add(col, 0, col * size, 0, size, size);
    }
  }

  private static drawSword(ctx: Ctx, ox: number, oy: number, variant: number): void {
    // Handle
    rect(ctx, ox + 7, oy + 12, 2, 4, '#6b4423');
    // Guard
    rect(ctx, ox + 5, oy + 11, 6, 1, '#aaaaaa');
    // Blade
    rect(ctx, ox + 7, oy + 2, 2, 10, '#cccccc');
    rect(ctx, ox + 6, oy + 3, 1, 8, '#aaaaaa');
    rect(ctx, ox + 9, oy + 3, 1, 8, '#dddddd'); // highlight
    // Tip
    px(ctx, ox + 7, oy + 1, '#dddddd');
    px(ctx, ox + 8, oy + 1, '#eeeeee');
    px(ctx, ox + 7, oy + 0, '#ffffff');

    // Variant coloring (level indicator)
    if (variant >= 1) {
      px(ctx, ox + 5, oy + 11, '#e8a038'); // gold guard
      px(ctx, ox + 10, oy + 11, '#e8a038');
    }
    if (variant >= 2) {
      px(ctx, ox + 7, oy + 5, '#88ccff'); // blue enchant
      px(ctx, ox + 8, oy + 7, '#88ccff');
    }
    if (variant >= 3) {
      px(ctx, ox + 7, oy + 0, '#ff4444'); // red tip
      px(ctx, ox + 8, oy + 1, '#ff4444');
    }
  }
}
